import {
  SheetActions,
  SheetHeader,
  SheetTitle,
  usePreventSheetClose,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { useMemo, useState } from "react";
import {
  ExpenseReportStatus,
  ExpenseReportsByViewDocument,
  UserRole,
  useDeleteReportMutation,
  useExpenseReportQuery,
  useUpsertExpenseReportMutation,
} from "@common/utils/graphql";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/utils/utils";
import { ViewOrEditReportExpense } from "./ViewOrEditReportExpense";
import { ExpenseCard } from "../components/ExpenseCard";
import { formatDate } from "@common/components/utils";
import { useToast } from "@/components/ui/use-toast";
import { AddExpenseToReport } from "./AddExpenseToReport";
import { Body } from "@hypercard/web-components";
import { useSubmitDraftReport } from "../components/useSubmitDraftReport";
import { useRecoilValue } from "recoil";
import { roleState } from "@common/state/roleState";
import { format } from "date-fns";
import { StatusText } from "@common/components/StatusText";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";

interface ViewOrEditReportProps {
  id: number;
  isEditModeDefault: boolean;
  onClose: () => void;
  expenseId?: number;
}

export const ViewOrEditReportSheetContent = ({
  id,
  isEditModeDefault,
  onClose,
  expenseId,
}: ViewOrEditReportProps) => {
  const { loading, data, refetch } = useExpenseReportQuery({
    variables: {
      reportId: id,
    },
  });
  const role = useRecoilValue(roleState);
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");

  const report = useMemo(() => {
    if (data?.expenseReport?.__typename !== "QueryExpenseReportSuccess") {
      return undefined;
    }
    const r = data?.expenseReport?.data;
    setTitle(r.title);
    setNotes(r.notes || "");
    return r;
  }, [data]);

  const expenseCount = report?.expenses?.length || 0;

  const [addExpenseToReport, setAddExpenseToReport] = useState(false);
  const [isEditMode, setIsEditMode] = useState(isEditModeDefault || false);
  const [upsertExpenseReport] = useUpsertExpenseReportMutation();
  const [deleteDraftExpenseReport] = useDeleteReportMutation({
    refetchQueries: [ExpenseReportsByViewDocument],
  });
  const {
    onSubmitDraftReport,
    isReportOutOfPolicy,
    loading: submitting,
  } = useSubmitDraftReport(id, onClose);
  const [selectedExpenseId, setSelectedExpenseId] = useState<
    number | undefined
  >(expenseId);

  usePreventSheetClose(() => isEditMode);

  if (loading || !report) {
    return (
      <div className="flex h-[calc(100%)] flex-col items-center justify-center gap-5">
        {loading ? (
          <FullPageLoader />
        ) : (
          <p className="text-center">
            There was an error loading more details about your report. Please
            contact Hypercard support.
          </p>
        )}
      </div>
    );
  }

  if (addExpenseToReport) {
    return (
      <AddExpenseToReport
        onCloseView={() => setAddExpenseToReport(false)}
        reportId={report.id}
      />
    );
  }
  const selectedExpense = report.expenses.find(
    ({ id }) => id === selectedExpenseId,
  );
  if (selectedExpense) {
    return (
      <ViewOrEditReportExpense
        onBack={() => {
          setSelectedExpenseId(undefined);
          refetch();
        }}
        expense={selectedExpense}
        totalExpenses={report.expenses.length}
        allowEdit={report.status === ExpenseReportStatus.Draft}
      />
    );
  }

  const onDeleteDraftReportExpense = async () => {
    try {
      const { data } = await deleteDraftExpenseReport({
        variables: {
          id: report.id,
        },
      });
      if (
        data?.deleteDraftExpenseReport.__typename !==
        "MutationDeleteDraftExpenseReportSuccess"
      ) {
        throw new Error();
      }
      onClose();
      toast({
        title: "Report deleted successfully",
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to delete report, please try again",
      });
    }
  };

  const header = (
    <SheetHeader
      className={cn(
        "flex flex-col gap-y-4 bg-transparent",
        !isEditMode && "border-b",
      )}
    >
      {!isEditMode && (
        <div className="flex h-5 items-center gap-x-3">
          <time
            className="text-body-sm text-white/70"
            dateTime={format(report.createdAt, "yyyy-MM-dd")}
          >
            {formatDate(report.createdAt)}
          </time>
          <Separator orientation="vertical" className="h-3 bg-white/30" />
          <StatusText {...report} size="sm" />
        </div>
      )}
      <SheetTitle>{isEditMode ? "Edit Report" : report.title}</SheetTitle>
      {!isEditMode && (
        <Body
          size="lg"
          weight="regular"
          className="mb-6 flex items-center gap-x-3"
        >
          <span>
            {expenseCount} Expense
            {expenseCount !== 1 ? "s" : ""}
          </span>
          <Separator orientation="vertical" className="h-6 bg-white/30" />
          <span>{report.displayTotalDollarAmount}</span>
        </Body>
      )}
    </SheetHeader>
  );

  const actions = (
    <SheetActions className="static">
      {report.status === ExpenseReportStatus.Draft &&
        (isEditMode ? (
          <div className="space-y-4">
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => setAddExpenseToReport(true)}
            >
              Add Expense
            </Button>
            <Button
              className="w-full"
              onClick={async () => {
                await upsertExpenseReport({
                  variables: {
                    reportId: report.id,
                    title,
                    notes,
                  },
                });
                setIsEditMode(false);
                refetch();
              }}
            >
              Save
            </Button>
          </div>
        ) : (
          <>
            <div className="flex gap-4 pb-4">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setTitle(report.title);
                  setNotes(report.notes || "");
                  setIsEditMode(true);
                }}
              >
                Edit Report
              </Button>
              <Button
                variant="destructive"
                className="w-full"
                onClick={onDeleteDraftReportExpense}
              >
                Delete
              </Button>
            </div>
            <Button
              className="w-full"
              onClick={onSubmitDraftReport}
              disabled={report.expenses.length === 0 || submitting}
            >
              {submitting
                ? "Submitting..."
                : isReportOutOfPolicy
                  ? "Submit Anyways"
                  : "Submit Report"}
            </Button>
          </>
        ))}
    </SheetActions>
  );

  const hasDesignatedReviewer =
    report?.status === ExpenseReportStatus.Pending &&
    role === UserRole.Employee;

  const metadata = (
    <div
      className={
        "mx-sheet-gap mb-6 flex flex-col gap-3 border-b pb-6 text-body-sm"
      }
    >
      {isEditMode && (
        <div className="space-y-2">
          <Label>Title</Label>
          <Input
            type="text"
            value={title}
            className="max-w-[100%]"
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      )}
      <div className="space-y-2">
        <Label>Memo</Label>
        {isEditMode ? (
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        ) : (
          <Body size="md" weight="regular">
            {report.notes || "No notes"}
          </Body>
        )}
      </div>
      {!isEditMode && report.employerNotes && (
        <div className="space-y-2">
          <Label>
            {report.isAutoApproved ? "Auto-Approval Reason" : "Employer Notes"}
          </Label>
          <Body size="md" weight="regular">
            {report.employerNotes}
          </Body>
        </div>
      )}
      {!isEditMode && hasDesignatedReviewer && (
        <div className="space-y-2">
          <Label>Designated Reviewer</Label>
          <Body size="md" weight="regular">
            {report.designatedApprover?.displayName ?? "Any Admin"}
          </Body>
        </div>
      )}
    </div>
  );

  const summary = (
    <div
      className={
        "flex flex-grow flex-col gap-3 overflow-auto bg-transparent px-sheet-gap pb-4 scrollbar-thin"
      }
    >
      <Label>Expenses</Label>
      {report.expenses.map((expense) => (
        <ExpenseCard
          key={expense.id}
          expense={expense}
          reportId={report.id}
          isEditMode={isEditMode}
          viewExpense={setSelectedExpenseId}
        />
      ))}
      {report.expenses.length === 0 && (
        <div className="text-base">No expenses recorded</div>
      )}
    </div>
  );

  return (
    <>
      {header}
      {metadata}
      {summary}
      {actions}
    </>
  );
};
